import { compact, first, isEmpty } from 'lodash';
import { useMemo } from 'react';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  ContextPrimaryClient,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';
import { diagnostics } from '@/utils/diagnostics';

export const BOTH_GRANTORS_SENTINEL = '__BOTH_GRANTORS__';

/**
 * A pure version of the useDualGrantorOptions hook.
 *
 * @param primaryClients - primaryClients from the useHouseholdDetailsContext hook
 * @param displayBothOptionFirst - Whether to display the "both" option first
 * @returns The options for a dual grantor select input
 */
export function getDualGrantorOptions(
  primaryClients: ContextPrimaryClient[] | null,
  displayBothOptionFirst: boolean = false
): SelectInputOption<string>[] {
  if (!primaryClients) {
    return [];
  }

  // Filter out any null/undefined clients
  const validClients = primaryClients.filter(Boolean);
  const firstClient = first(validClients);

  if (isEmpty(validClients)) {
    return [];
  } else if (validClients.length === 1) {
    if (!firstClient) return [];
    return [
      {
        display: firstClient.displayName,
        value: firstClient.id,
      },
    ];
  }

  const clientOptions: SelectInputOption<string>[] = validClients.map(
    (client) => ({
      display: client.displayName,
      value: client.id,
    })
  );

  const bothGrantorsOption: SelectInputOption<string> = {
    display: clientOptions.map((c) => c.display).join(' & '),
    value: BOTH_GRANTORS_SENTINEL,
  };

  if (displayBothOptionFirst) return [bothGrantorsOption, ...clientOptions];

  return [...clientOptions, bothGrantorsOption];
}

/**
 * Returns the options for a dual grantor select input.
 *
 * Single-grantor: `["John Doe"]`
 *
 * Dual-grantor: `["John Doe", "Jane Doe", "John Doe & Jane Doe"]`
 *
 * @param displayBothOptionFirst - Whether to display the "both" option first; defaults to displaying last
 * @returns The options for a dual grantor select input
 */
export function useDualGrantorOptions(
  displayBothOptionFirst: boolean = false
): SelectInputOption<string>[] {
  const { primaryClients } = useHouseholdDetailsContext();

  return useMemo(() => {
    return getDualGrantorOptions(primaryClients, displayBothOptionFirst);
  }, [displayBothOptionFirst, primaryClients]);
}

export function getSerializedDualGrantorOption(
  input: string,
  primaryClients: ContextPrimaryClient[] | null
): string[] {
  if (input === BOTH_GRANTORS_SENTINEL) {
    return compact(primaryClients?.map((c) => c.id) ?? []);
  }

  return [input];
}

/**
 * @param input - The input to deserialize; can be a single concat'ed string (split with `splitSubstring`) or an array of values
 * @param splitSubstring - The substring to split the input on; this is useful if two grantor IDs are written into a single field
 * @returns The deserialized input
 */
export function getDeserializedDualGrantorOption(
  input: string[] | string,
  splitSubstring?: string
): string {
  if (Array.isArray(input)) {
    if (input.length > 2) {
      diagnostics.warn(
        'getDeserializedDualGrantorOption: More than two values found in input',
        {
          input,
        }
      );
      return BOTH_GRANTORS_SENTINEL;
    }
    if (input.length === 2) {
      return BOTH_GRANTORS_SENTINEL;
    }

    return first(input) || '';
  } else if (splitSubstring) {
    const values = input.split(splitSubstring);
    if (values.length > 2) {
      diagnostics.warn(
        'getDeserializedDualGrantorOption: More than two values found in input',
        {
          input,
          splitSubstring,
        }
      );
      return BOTH_GRANTORS_SENTINEL;
    }
    if (values.length === 2) {
      return BOTH_GRANTORS_SENTINEL;
    }
    return first(values) || '';
  }
  return input;
}
